import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Send, Sparkles, X } from "lucide-react";
import { api, formatApiError } from "../../lib/api";

export const CopilotDock = ({ title = "Copiloto OMNIA", context = "", suggestions = [] }) => {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, busy]);

  const ask = async (q) => {
    const question = (q || input).trim();
    if (!question || busy) return;
    setInput("");
    setMsgs((m) => [...m, { role: "user", text: question }]);
    setBusy(true);
    try {
      const { data } = await api.post("/ai/copilot", { question: context ? `${context}\n\n${question}` : question });
      setMsgs((m) => [...m, { role: "assistant", text: data.answer || "" }]);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "El copiloto no pudo responder");
      setMsgs((m) => m.slice(0, -1));
    } finally { setBusy(false); }
  };

  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="copilot-dock-open"
        className="fixed bottom-6 right-6 z-[60] rounded-full bg-gradient-violet text-white text-sm pl-4 pr-5 py-3 shadow-lg hover:opacity-90 inline-flex items-center gap-2">
        <Sparkles className="w-4 h-4" /> Copilot
      </button>
      {open && (
        <div className="fixed inset-y-0 right-0 z-[80] w-full sm:w-[420px] bg-cream border-l border-navy/10 shadow-2xl flex flex-col" data-testid="copilot-dock">
          <div className="flex items-center gap-2 px-4 py-3 border-b border-navy/10 bg-white">
            <Sparkles className="w-4 h-4 text-violet" />
            <div className="font-medium text-sm">{title}</div>
            <button onClick={() => setOpen(false)} className="ml-auto text-navy/40 hover:text-navy" data-testid="copilot-dock-close"><X className="w-4 h-4" /></button>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {msgs.length === 0 && (
              <div className="space-y-2">
                <p className="text-sm text-navy/50">Pregúntame lo que quieras sin salir de este módulo.</p>
                {suggestions.map((s) => (
                  <button key={s} onClick={() => ask(s)} data-testid="copilot-dock-suggestion"
                    className="block w-full text-left text-sm rounded-lg border border-navy/10 bg-white px-3 py-2.5 hover:border-violet transition-colors">{s}</button>
                ))}
              </div>
            )}
            {msgs.map((m, i) => (
              <div key={i} className={`text-sm whitespace-pre-wrap rounded-xl px-3.5 py-2.5 max-w-[92%] ${m.role === "user" ? "bg-navy text-cream ml-auto" : "bg-white border border-navy/10"}`}
                data-testid={m.role === "user" ? "copilot-dock-msg-user" : "copilot-dock-msg-assistant"}
                dangerouslySetInnerHTML={{ __html: m.text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/\*\*(.+?)\*\*/g, "<b>$1</b>").replace(/^#+\s?(.+)$/gm, "<b>$1</b>") }} />
            ))}
            {busy && <div className="flex items-center gap-2 text-xs text-navy/40"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Analizando…</div>}
            <div ref={endRef} />
          </div>
          <form onSubmit={(e) => { e.preventDefault(); ask(); }} className="p-3 border-t border-navy/10 bg-white flex gap-2">
            <input className="flex-1 bg-white border border-navy/10 rounded-full px-4 py-2.5 text-sm outline-none focus:border-violet"
              placeholder="Escribe tu pregunta…" value={input} onChange={(e) => setInput(e.target.value)} data-testid="copilot-dock-input" />
            <button disabled={busy || !input.trim()} data-testid="copilot-dock-send"
              className="rounded-full bg-gradient-violet text-white w-10 h-10 flex items-center justify-center hover:opacity-90 disabled:opacity-50">
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      )}
    </>
  );
};
